// src/components/TextCompare/ComparisonSummary.tsx

import React, { useState, useMemo } from 'react';
import {
    Zap, Sparkles, AlertTriangle, ShieldAlert, Info,
    ArrowUpRight, Languages, Palette, Scale, Banknote, CheckCircle2, Activity
} from 'lucide-react';

export interface ImpactFlag {
    type: 'CRITICAL' | 'WARNING' | 'INFO';
    category: string;
    label: string;
    description: string;
    line?: number;
}

interface ComparisonSummaryProps {
    summary: string;
    impactFlags?: ImpactFlag[];
    isLoading: boolean;
    onRegenerate: () => void;
    onJumpToLine: (line: number) => void;
}

const ComparisonSummary: React.FC<ComparisonSummaryProps> = ({
    summary,
    impactFlags = [],
    isLoading,
    onRegenerate,
    onJumpToLine
}) => {
    const [filter, setFilter] = useState<'ALL' | 'CRITICAL' | 'WARNING' | 'INFO'>('ALL');

    // Statistics for summary chips (non-filtering)
    const stats = useMemo(() => {
        return {
            total: impactFlags.length,
            critical: impactFlags.filter(f => f.type === 'CRITICAL').length,
            warning: impactFlags.filter(f => f.type === 'WARNING').length,
            info: impactFlags.filter(f => f.type === 'INFO').length
        };
    }, [impactFlags]);

    const displayedFlags = useMemo(() => {
        if (filter === 'ALL') return impactFlags;
        return impactFlags.filter(f => f.type === filter);
    }, [impactFlags, filter]);

    const getFlagIcon = (category: string | undefined, type: string) => {
        const cat = category?.toUpperCase();
        switch (cat) {
            case 'GRAMMAR': return <Languages size={14} />;
            case 'STYLE': return <Palette size={14} />;
            case 'LEGAL': return <Scale size={14} />;
            case 'FINANCE': return <Banknote size={14} />;
            case 'ACCURACY': return <CheckCircle2 size={14} />;
            case 'SECURITY': return <ShieldAlert size={14} />;
            case 'LOGIC': return <Zap size={14} />;
            case 'TONE': return <Palette size={14} />;
            default:
                if (type === 'CRITICAL') return <ShieldAlert size={14} />;
                if (type === 'WARNING') return <AlertTriangle size={14} />;
                return <Info size={14} />;
        }
    };

    const handleFilterToggle = (newFilter: 'CRITICAL' | 'WARNING' | 'INFO') => {
        setFilter(prev => prev === newFilter ? 'ALL' : newFilter);
    };

    return (
        <div className={`mb-6 animate-[fadeIn_0.4s_ease-out] ${isLoading ? 'opacity-80' : ''}`}>
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.05)]">
                <div className="flex justify-between items-center py-3 px-4 bg-slate-50 border-b border-slate-100">
                    <div className={`flex items-center gap-2 py-0.5 px-2.5 -ml-2 rounded-lg select-none ${isLoading ? 'opacity-80' : ''}`}>
                        <div
                            className={`w-6 h-6 bg-slate-100 text-indigo-500 rounded-md flex items-center justify-center transition-all duration-200 cubic-bezier(0.175,0.885,0.32,1.275) ${isLoading ? 'cursor-default bg-slate-100' : 'cursor-pointer hover:bg-indigo-100 hover:scale-[1.15] hover:text-indigo-600 hover:shadow-[0_4px_12px_rgba(99,102,241,0.15)] active:scale-95'}`}
                            onClick={isLoading ? undefined : onRegenerate}
                            title={isLoading ? "Analyzing..." : "Regenerate Analysis"}
                        >
                            <Sparkles size={14} className={isLoading ? 'animate-[slow-blink_2s_infinite]' : ''} />
                        </div>
                        <h3 className="m-0 text-[0.9rem] font-semibold text-slate-800 tracking-tight">Deep Analysis</h3>
                    </div>

                    {!isLoading && stats.total > 0 && (
                        <div className="flex items-center gap-2">
                            <button
                                className={`py-0.5 px-2.5 rounded-full text-[0.75rem] font-semibold flex items-center gap-1 cursor-pointer border-none transition-all duration-200 ${filter === 'ALL' ? 'bg-[#FCFCFC] text-slate-800' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                                onClick={() => setFilter('ALL')}
                            >
                                <Activity size={12} /> {stats.total} Insights
                            </button>

                            {stats.critical > 0 && (
                                <button
                                    className={`py-0.5 px-2.5 rounded-full text-[0.75rem] font-semibold flex items-center gap-1 cursor-pointer border-none transition-all duration-200 ${filter === 'CRITICAL' ? 'bg-red-500 text-white' : 'bg-red-50 text-red-500 hover:bg-slate-200 hover:brightness-95'}`}
                                    onClick={() => handleFilterToggle('CRITICAL')}
                                >
                                    {stats.critical} Critical
                                </button>
                            )}

                            {stats.warning > 0 && (
                                <button
                                    className={`py-0.5 px-2.5 rounded-full text-[0.75rem] font-semibold flex items-center gap-1 cursor-pointer border-none transition-all duration-200 ${filter === 'WARNING' ? 'bg-amber-600 text-white' : 'bg-amber-50 text-amber-600 hover:bg-slate-200 hover:brightness-95'}`}
                                    onClick={() => handleFilterToggle('WARNING')}
                                >
                                    {stats.warning} Warning
                                </button>
                            )}

                            {stats.info > 0 && (
                                <button
                                    className={`py-0.5 px-2.5 rounded-full text-[0.75rem] font-semibold flex items-center gap-1 cursor-pointer border-none transition-all duration-200 ${filter === 'INFO' ? 'bg-sky-600 text-white' : 'bg-sky-50 text-sky-600 hover:bg-slate-200 hover:brightness-95'}`}
                                    onClick={() => handleFilterToggle('INFO')}
                                >
                                    {stats.info} Info
                                </button>
                            )}
                        </div>
                    )}
                </div>

                <div className="p-4">
                    {isLoading ? (
                        <div className="flex flex-col gap-3">
                            <div className="h-10 rounded-md bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100 bg-[length:400%_100%] animate-[skeleton-shimmer_1.4s_ease_infinite] relative overflow-hidden"></div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="h-20 rounded-lg bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100 bg-[length:400%_100%] animate-[skeleton-shimmer_1.4s_ease_infinite] relative overflow-hidden delay-100"></div>
                                <div className="h-20 rounded-lg bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100 bg-[length:400%_100%] animate-[skeleton-shimmer_1.4s_ease_infinite] relative overflow-hidden delay-200"></div>
                            </div>
                        </div>
                    ) : (
                        <>
                            {summary && filter === 'ALL' && (
                                <div className="mb-3 pb-3 border-b border-dashed border-slate-200">
                                    <p className="m-0 text-[0.875rem] leading-[1.6] text-slate-700 font-[450]">{summary}</p>
                                </div>
                            )}

                            {displayedFlags.length > 0 && (
                                <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-3">
                                    {displayedFlags.map((flag, idx) => (
                                        <div key={idx} className={`bg-slate-50 border border-slate-100 rounded-lg p-2.5 flex flex-col gap-1.5 transition-all duration-200 hover:border-slate-200 hover:bg-slate-50 hover:shadow-[0_2px_4px_rgba(0,0,0,0.02)]
                                            ${flag.type === 'CRITICAL' ? '[&_.indicator]:text-red-600 [&_.type-tag]:text-red-600' : ''}
                                            ${flag.type === 'WARNING' ? '[&_.indicator]:text-amber-600 [&_.type-tag]:text-amber-600' : ''}
                                            ${flag.type === 'INFO' ? '[&_.indicator]:text-blue-600' : ''}
                                        `}>
                                            <div className="flex items-center gap-2">
                                                <div className="indicator w-6 h-6 rounded-md flex items-center justify-center bg-white shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                                                    {getFlagIcon(flag.category, flag.type)}
                                                </div>
                                                <div className="flex gap-1 flex-1">
                                                    <span className="type-tag text-[0.65rem] font-bold uppercase text-slate-400">{flag.type}</span>
                                                    <span className="text-[0.65rem] font-semibold text-slate-500 bg-slate-100 px-1 rounded-[3px]">{flag.category}</span>
                                                </div>
                                                {flag.line && (
                                                    <button
                                                        className="text-[0.7rem] font-bold text-slate-400 bg-transparent border-none flex items-center gap-[2px] cursor-pointer py-[2px] px-1 rounded hover:text-indigo-500 hover:bg-indigo-50"
                                                        onClick={() => onJumpToLine(flag.line!)}
                                                    >
                                                        L{flag.line} <ArrowUpRight size={10} />
                                                    </button>
                                                )}
                                            </div>
                                            <h5 className="m-0 text-[0.85rem] font-semibold text-slate-800">{flag.label}</h5>
                                            <p className="m-0 text-[0.8rem] text-slate-500 leading-[1.4]">{flag.description}</p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ComparisonSummary;
